import React, { useState, useEffect } from 'react';

/**
 * Profile Editor Component
 * Allows users to edit their name and pronouns
 */
export default function ProfileEditor({ initialName, initialPronouns, initialEmail, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(initialName || '');
  const [pronouns, setPronouns] = useState(initialPronouns || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Update state when props change
  useEffect(() => {
    setName(initialName || '');
    setPronouns(initialPronouns || '');
  }, [initialName, initialPronouns]);

  const handleSave = async () => {
    setError('');
    setIsSaving(true);

    try {
      const supabase = window.supabaseClient;
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Update profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          name: name.trim(),
          pronouns: pronouns.trim() || null
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // Update localStorage
      const authState = JSON.parse(localStorage.getItem('pinkWalletAuth') || '{}');
      authState.name = name.trim();
      authState.pronouns = pronouns.trim();
      localStorage.setItem('pinkWalletAuth', JSON.stringify(authState));

      // Dispatch event to update UI
      window.dispatchEvent(new CustomEvent('authStateChanged', { detail: authState }));

      // Call parent callback
      if (onSave) {
        onSave({ name: name.trim(), pronouns: pronouns.trim() });
      }

      setIsEditing(false);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err.message || 'Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setName(initialName || '');
    setPronouns(initialPronouns || '');
    setError('');
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <p className="text-lg">{name || 'Not set'}</p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pronouns</label>
          <p className="text-lg">{pronouns || 'Not set'}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <p className="text-lg">{initialEmail || 'Not set'}</p>
        </div>
        <button
          onClick={() => setIsEditing(true)}
          className="text-sm font-medium text-brand-700 hover:text-brand-800 px-3 py-1 border border-brand-200 rounded hover:bg-brand-50"
        >
          Edit Profile
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="edit-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          placeholder="Enter your name"
          disabled={isSaving}
          required
        />
      </div>

      <div>
        <label htmlFor="edit-pronouns" className="block text-sm font-medium text-gray-700 mb-1">
          Pronouns <span className="text-gray-500 text-sm font-normal">(Optional)</span>
        </label>
        <input
          type="text"
          id="edit-pronouns"
          value={pronouns}
          onChange={(e) => setPronouns(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          placeholder="e.g., she/her, he/him, they/them"
          disabled={isSaving}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <p className="text-lg text-gray-500">{initialEmail || 'Not set'}</p>
        <p className="text-xs text-gray-500 mt-1">Email cannot be changed here</p>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={isSaving || !name.trim()}
          className="bg-brand-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-brand-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className="text-gray-700 font-medium py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
